import React from "react";
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { companyLogoUrl } from "../../../apis/company-logo-apis/companyLogoApis";
import { PAGE_MAX_WIDTH } from "../styles/publicStyles";

// ⌘ on Macs, Ctrl elsewhere, for the search hint.
const isMac =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform || "");

// Height of the bar's main row; the tabs' underline sits on its bottom edge.
const ROW = 64;

const Kbd = ({ children }) => {
  const theme = useTheme();
  return (
    <Box
      component="kbd"
      sx={{
        fontFamily: "inherit",
        fontSize: 11.5,
        fontWeight: 500,
        lineHeight: 1,
        px: 0.75,
        py: 0.5,
        borderRadius: "6px",
        border: `1px solid ${theme.trust.line}`,
        backgroundColor: theme.trust.surface,
        color: theme.trust.muted,
      }}
    >
      {children}
    </Box>
  );
};

// The company's own brand at the top: its logo (or initial) and its name.
// Goes to the Overview. Regodit appears
// only in the footer ("Powered by").
const CompanyBrand = ({ company, onHome }) => {
  const theme = useTheme();
  const t = theme.trust;
  const logo = companyLogoUrl(company.logoPath);
  return (
    <ButtonBase
      onClick={onHome}
      aria-label={`${company.name} Trust Center home`}
      sx={{
        gap: 1.25,
        borderRadius: "8px",
        py: 0.5,
        minWidth: 0,
        flexShrink: 1,
        "&.Mui-focusVisible": {
          outline: `2px solid ${t.accent}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: "8px",
          display: "grid",
          placeItems: "center",
          overflow: "hidden",
          border: logo ? `1px solid ${t.line}` : "none",
          backgroundColor: logo ? t.surface : t.accent,
        }}
      >
        {logo ? (
          <Box
            component="img"
            src={logo}
            alt=""
            sx={{ width: "80%", height: "80%", objectFit: "contain" }}
          />
        ) : (
          <Typography
            aria-hidden="true"
            sx={{ color: "#fff", fontWeight: 600, fontSize: 15 }}
          >
            {(company.name || "?").trim().charAt(0).toUpperCase()}
          </Typography>
        )}
      </Box>
      <Typography
        noWrap
        sx={{
          fontSize: 15,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: t.ink,
          minWidth: 0,
        }}
      >
        {company.name}
      </Typography>
    </ButtonBase>
  );
};

// One bar across the top: company brand, the sections (an underline in the
// secondary colour marks the open one), search (⌘K) and Request access. Its
// content lines up with the page below; a soft shadow once the page has
// scrolled. On narrow screens the sections get their own row, which scrolls
// sideways.
const TrustNavBar = ({
  company,
  tabs,
  tab,
  onTab,
  onSearch,
  onRequest,
  verifiedEmail,
  onSignOut,
  scrolled,
}) => {
  const theme = useTheme();
  const t = theme.trust;

  const tabsEl = (
    <Tabs
      value={tab}
      onChange={(_, next) => onTab(next)}
      variant="scrollable"
      scrollButtons={false}
      allowScrollButtonsMobile={false}
      aria-label="Trust Center sections"
      TabIndicatorProps={{
        sx: {
          height: 2,
          borderRadius: "2px 2px 0 0",
          backgroundColor: t.secondary,
        },
      }}
      sx={{
        minHeight: { xs: 48, lg: ROW },
        minWidth: 0,
        "& .MuiTabs-flexContainer": { gap: { xs: 2.5, lg: 3 }, height: "100%" },
        "& .MuiTabs-scroller": { display: "flex" },
        "& .MuiTab-root": {
          minHeight: { xs: 48, lg: ROW },
          minWidth: 0,
          px: 0,
          textTransform: "none",
          fontSize: 14,
          fontWeight: 400,
          color: t.inkSoft,
          transition: `color 160ms ${t.ease}`,
          "&:hover": { color: t.ink },
          "&.Mui-focusVisible": {
            outline: `2px solid ${t.accent}`,
            outlineOffset: -6,
            borderRadius: "6px",
          },
        },
        "& .MuiTab-root.Mui-selected": { color: t.ink, fontWeight: 600 },
      }}
    >
      {tabs.map((item) => (
        <Tab key={item.id} value={item.id} label={item.label} disableRipple />
      ))}
    </Tabs>
  );

  return (
    <Box
      component="nav"
      aria-label="Trust Center"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 5,
        backgroundColor: alpha(t.surface, 0.94),
        backdropFilter: "saturate(1.4) blur(12px)",
        borderBottom: `1px solid ${t.line}`,
        boxShadow: scrolled
          ? "0 6px 24px -18px rgba(18, 26, 58, 0.35)"
          : "none",
        transition: `box-shadow 240ms ${t.ease}`,
      }}
    >
      {/* Same width and side padding as the page content. On wide screens
          one row (brand, sections, actions); below that the sections wrap
          onto their own row. Rendered once, placed by CSS. */}
      <Box
        sx={{
          maxWidth: PAGE_MAX_WIDTH,
          width: "100%",
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 },
          boxSizing: "border-box",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          columnGap: 3,
        }}
      >
        <Box
          sx={{
            order: 1,
            minWidth: 0,
            flexShrink: 1,
            display: "flex",
            alignItems: "center",
            height: { xs: 56, lg: ROW },
          }}
        >
          <CompanyBrand company={company} onHome={() => onTab("overview")} />
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ flexShrink: 0, order: { xs: 2, lg: 3 }, ml: "auto" }}
        >
          {/* Search: a compact field-like button on wide screens, an icon on
              phones. */}
          <ButtonBase
            onClick={onSearch}
            aria-label="Search this Trust Center"
            aria-keyshortcuts={isMac ? "Meta+K" : "Control+K"}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              alignItems: "center",
              gap: 1,
              height: 36,
              pl: 1.25,
              pr: 0.75,
              minWidth: 150,
              borderRadius: "8px",
              border: `1px solid ${t.line}`,
              backgroundColor: t.paper,
              color: t.muted,
              fontSize: 14,
              transition: `border-color 160ms ${t.ease}, background-color 160ms ${t.ease}`,
              "&:hover": {
                borderColor: t.lineStrong,
                backgroundColor: t.surface,
              },
              "&.Mui-focusVisible": {
                outline: `2px solid ${t.accent}`,
                outlineOffset: 2,
              },
            }}
          >
            <SearchRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} />
            <Box component="span" sx={{ flex: 1, textAlign: "left" }}>
              Search
            </Box>
            <Kbd>{isMac ? "⌘K" : "Ctrl K"}</Kbd>
          </ButtonBase>
          <Tooltip title="Search" arrow>
            <IconButton
              onClick={onSearch}
              aria-label="Search this Trust Center"
              sx={{
                display: { xs: "inline-flex", sm: "none" },
                color: t.inkSoft,
              }}
            >
              <SearchRoundedIcon />
            </IconButton>
          </Tooltip>

          {verifiedEmail ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ minWidth: 0 }}
            >
              <Tooltip title={`Signed in as ${verifiedEmail}`} arrow>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{
                    height: 36,
                    px: 1.25,
                    borderRadius: "8px",
                    backgroundColor: t.verifiedSoft,
                    color: t.verified,
                    maxWidth: { xs: 140, md: 240 },
                  }}
                >
                  <VerifiedUserOutlinedIcon
                    aria-hidden="true"
                    sx={{ fontSize: 17 }}
                  />
                  <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 500 }}>
                    {verifiedEmail}
                  </Typography>
                </Stack>
              </Tooltip>
              {onSignOut && (
                <Button
                  size="small"
                  onClick={onSignOut}
                  sx={{ color: t.muted, minWidth: 0, whiteSpace: "nowrap" }}
                >
                  Sign out
                </Button>
              )}
            </Stack>
          ) : (
            onRequest && (
              <Button
                variant="contained"
                onClick={onRequest}
                startIcon={
                  <LockOutlinedIcon sx={{ fontSize: "16px !important" }} />
                }
                sx={{
                  height: 36,
                  whiteSpace: "nowrap",
                  px: { xs: 1.5, sm: 1.75 },
                  fontSize: 14,
                  borderRadius: "8px",
                  "& .label-long": { display: { xs: "none", sm: "inline" } },
                }}
              >
                Request<span className="label-long">&nbsp;access</span>
              </Button>
            )
          )}
        </Stack>
        <Box
          sx={{
            order: { xs: 3, lg: 2 },
            flex: { xs: "1 1 100%", lg: "1 1 auto" },
            minWidth: 0,
            display: "flex",
            justifyContent: { xs: "flex-start", lg: "center" },
            alignSelf: "stretch",
            position: "relative",
            // Phones: the row of sections fades at the right edge, so it is
            // clear there are more to scroll to.
            "&::after": {
              content: '""',
              display: { xs: "block", lg: "none" },
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              width: 32,
              pointerEvents: "none",
              background: `linear-gradient(90deg, ${alpha(t.surface, 0)}, ${t.surface})`,
            },
          }}
        >
          {tabsEl}
        </Box>
      </Box>
    </Box>
  );
};

export default TrustNavBar;
